import { AlertTriangle, Loader2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button, Modal, Select } from 'slab'
import {
  useGetAdminMergeEmployerIsolvedClients,
  usePutAdminMergeEmployerUpdateCompanyId,
} from 'utils/web_api'
import { type AdminMergeIsolvedClient } from 'utils/web_api.schemas'

type MergeIsolvedClientSelectorProps = {
  employerId: string
  companyIds: string[]
  employerName?: string
}

export const MergeIsolvedClientSelector: React.FC<MergeIsolvedClientSelectorProps> = ({
  employerId,
  companyIds,
  employerName = 'this employer'
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(companyIds[0] || '')
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Fetch available clients
  const { data: clientsData, isLoading: isLoadingClients } = useGetAdminMergeEmployerIsolvedClients(
    { employer_id: employerId }
  )

  // Update mutation
  const updateMutation = usePutAdminMergeEmployerUpdateCompanyId()

  // Update selected value when companyIds prop changes
  useEffect(() => {
    setSelectedCompanyId(companyIds[0] || '')
  }, [companyIds])

  const handleUpdateClick = () => {
    setIsModalOpen(true)
  }

  const handleConfirmUpdate = async () => {
    try {
      await updateMutation.mutateAsync({
        data: {
          employer_id: employerId,
          company_id: selectedCompanyId,
        }
      })

      toast.success('Done! A sync job has been initiated', {
        duration: 5000,
      })

      setIsModalOpen(false)
    } catch (error) {
      toast.error('Failed to update company ID')
      console.error('Error updating company ID:', error)
    }
  }

  const selectedClient = clientsData?.clients?.find(
    client => client.client_id === selectedCompanyId
  )

  const formatClientOption = (client: AdminMergeIsolvedClient) => {
    return `${client.client_name}, code: ${client.client_code}, id: ${client.client_id}`
  }

  const formatSelectedClient = () => {
    if (!selectedClient) return 'No client selected'
    return `${selectedClient.client_name} (Code: ${selectedClient.client_code}, ID: ${selectedClient.client_id})`
  }

  if (isLoadingClients) {
    return (
      <div className='flex items-center space-x-2'>
        <Loader2 className='h-4 w-4 animate-spin' />
        <span className='text-gray-500'>Loading clients...</span>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <div>
        <h3 className='text-lg font-semibold text-gray-900'>Merge Config - Company ID</h3>
        <p className='text-sm text-gray-500 mb-3'>Only isolved clients that are not already in use by another employer are shown.</p>

        <div className='flex items-end gap-4'>
          <div className='flex-1'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Select isolved Client
            </label>
            <Select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className='w-full'
            >
              <option value=''>No client (clear selection)</option>
              {clientsData?.clients?.map((client) => (
                <option key={client.client_id} value={client.client_id || ''}>
                  {formatClientOption(client)}
                </option>
              ))}
            </Select>
          </div>

          <Button
            onClick={handleUpdateClick}
            disabled={updateMutation.isPending}
            variant='primary'
          >
            {updateMutation.isPending
              ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Updating...
                </>
                )
              : (
                  'Update'
                )}
          </Button>
        </div>
      </div>

      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal.Header>
          <Modal.Title dismissible>
            <div className='flex items-center gap-2'>
              <AlertTriangle className='h-5 w-5 text-yellow-600' />
              Confirm Company ID Update
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            {selectedCompanyId
              ? (
                <>
                  <p>
                    You're about to set <strong>{employerName}</strong>'s iSolved client to:
                  </p>
                  <div className='bg-gray-50 p-3 rounded-md border border-gray-200'>
                    <p className='font-mono text-sm'>
                      {formatSelectedClient()}
                    </p>
                  </div>
                  <p className='text-red-600 font-semibold'>
                    Please be sure that this employer's isolved client is set correctly.
                    Setting it incorrectly will lead to another client's data being synced to this account,
                    which is a privacy issue. Be very careful!
                  </p>
                </>
                )
              : (
                <>
                  <p>
                    You're about to remove the iSolved client from <strong>{employerName}</strong>.
                  </p>
                  <p className='text-orange-600 font-semibold'>
                    Removing the client from this employer will disable syncs until a client is selected in the future.
                  </p>
                </>
                )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-between'>
            <Button
              variant='secondary'
              onClick={() => setIsModalOpen(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant='primary-destructive'
              onClick={handleConfirmUpdate}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending
                ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Updating...
                  </>
                  )
                : (
                    'Confirm Update'
                  )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
